import {
  applyConfigurationState,
  type ConfigurationAdapter,
  type TokaMapConfigurationState,
} from "./commands";
import type {
  Command,
  History,
  HistoryChangeKind,
} from "./History";

/** Voir `TokaMapConfigurationState` — un évènement porte toujours l'état COMPLET résultant de la
 * modification (vue ET styles par défaut), pas seulement le champ qui a effectivement changé, même
 * principe qu'un `SaveEvent` de forme qui porte le `Shape` entier. */
export interface SaveConfigurationEvent extends TokaMapConfigurationState {}

export type OnSaveConfiguration = (event: SaveConfigurationEvent) => Promise<void>;

/** Voir `SaveStatusEvent` (`ShapeSaveTracker`) — même sémantique, pour la configuration de la carte
 * plutôt que pour une forme (une seule "entité" ici, pas de `shapeId`). */
export interface SaveConfigurationStatusEvent {
  status: "pending" | "success" | "error";
}

export type SaveConfigurationStatusListener = (event: SaveConfigurationStatusEvent) => void;

/** Voir `PendingEntry` (`ShapeSaveTracker`) — même rôle. */
interface PendingEntry {
  command: Command;
}

/** Voir `CommandMeta` (`ShapeSaveTracker`) — même rôle, sans `shapeId`/`kind` (une seule entité,
 * toujours "mise à jour", jamais créée/supprimée). */
interface CommandMeta {
  /** Voir `ShapeSaveTracker`'s `CommandMeta.skipSave` — reporté sur `runHistoryChange` pour qu'un
   * undo/redo manuel d'une commande exécutée avec `skipSave` n'appelle pas non plus
   * `onSaveConfiguration` (reste local y compris à travers Annuler/Rétablir). */
  skipSave: boolean;
  skipConfirm: boolean;
  beforeConfiguration: TokaMapConfigurationState;
  afterConfiguration: TokaMapConfigurationState;
}

/**
 * Suivi de concurrence pour la sauvegarde optimiste avec rollback de la configuration globale de la
 * carte (vue de caméra par défaut ET style par défaut des nouvelles formes dessinées — voir
 * `TokaMapConfigurationState`) — même principe que `ShapeSaveTracker` (BESOINS.md §7.1), mais
 * simplifié pour une seule "entité" globale plutôt qu'une file par forme : une seule référence
 * confirmée, une seule file de commandes en vol, partagée entre `setMapView` et `setDefaultShapeStyle`
 * (voir `createTokaMap.ts`) — une modification de l'un de ces deux aspects déclenche
 * `onSaveConfiguration` avec la configuration ENTIÈRE, comme ils vivent tous les deux dans le même
 * objet `configurations` du JSON source.
 *
 * `confirmedConfiguration` retient la dernière configuration confirmée par une sauvegarde réussie —
 * `null` avant la toute première modification suivie (jamais ensuite : contrairement à une forme, la
 * configuration n'a pas d'équivalent "n'existe pas encore" — la configuration "d'avant" la toute
 * première commande sert de première référence, voir `run`/`trackAndSave`).
 */
export class ConfigurationSaveTracker {
  private confirmedConfiguration: TokaMapConfigurationState | null = null;
  private pendingCommands: PendingEntry[] = [];
  private commandMeta = new WeakMap<Command, CommandMeta>();
  private listeners = new Set<SaveConfigurationStatusListener>();
  /** Ancre "avant toute la rafale skipHistory en cours" — voir `resolveHistoryBeforeConfiguration`
   * (`ShapeSaveTracker.resolveHistoryBeforeShape`, même rôle, sans `shapeId` : une seule entité). */
  private historyAnchor: TokaMapConfigurationState | null = null;

  constructor(
    private mapAdapter: ConfigurationAdapter,
    private history: History,
    private onSaveConfiguration: OnSaveConfiguration | undefined,
  ) {}

  onStatusChange(listener: SaveConfigurationStatusListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Voir `ShapeSaveTracker.resolveHistoryBeforeShape` — même mécanisme, sans `shapeId` (une seule
   * entité globale, comme `confirmedConfiguration` ci-dessus) : capture `currentConfiguration` comme
   * ancre au premier `skipHistory: true` d'une rafale (ex. les ticks continus d'un slider zoom/
   * zoomMin/zoomMax, voir `SliderInputNumberFeatureStyle` côté consommateur), la restitue (et
   * l'efface) à la commande `skipHistory: false` qui clôt le geste — pour que `Annuler` défasse tout
   * le geste d'un coup plutôt que son dernier tick, quasi identique à l'état "après" sans ce suivi. */
  resolveHistoryBeforeConfiguration(
    currentConfiguration: TokaMapConfigurationState,
    skipHistory: boolean,
  ): TokaMapConfigurationState {
    if (skipHistory) {
      if (this.historyAnchor === null) this.historyAnchor = currentConfiguration;
      return currentConfiguration;
    }
    const anchor = this.historyAnchor;
    this.historyAnchor = null;
    return anchor ?? currentConfiguration;
  }

  /**
   * Exécute `command`, puis déclenche `onSaveConfiguration` en tâche de fond (sauf absence de
   * `onSaveConfiguration`, ou `skipSave`) — mêmes options que `ShapeSaveTracker.run` (voir
   * `TokaMapInstance.setMapView`) : `skipHistory` (pas de `History.execute`, juste `command.do()`,
   * jamais annulable), `skipSave` (n'appelle jamais `onSaveConfiguration` pour cette commande,
   * mais amorce quand même `confirmedConfiguration` si c'est la toute première), `skipConfirm` (même
   * en cas de succès, `confirmedConfiguration` n'avance pas jusqu'à `resultConfiguration`).
   */
  run(
    command: Command,
    skipSave: boolean,
    skipHistory: boolean,
    skipConfirm: boolean,
    confirmedConfigurationIfNew: TokaMapConfigurationState,
    resultConfiguration: TokaMapConfigurationState,
  ): void {
    if (skipHistory) {
      command.do();
    } else {
      this.history.execute(command);
      this.commandMeta.set(command, {
        skipSave,
        skipConfirm,
        beforeConfiguration: confirmedConfigurationIfNew,
        afterConfiguration: resultConfiguration,
      });
    }

    if (skipSave) {
      if (this.confirmedConfiguration === null) this.confirmedConfiguration = confirmedConfigurationIfNew;
      return;
    }

    this.trackAndSave(command, skipConfirm, confirmedConfigurationIfNew, resultConfiguration);
  }

  /** Voir `ShapeSaveTracker.runHistoryChange` — même rôle pour un Annuler/Rétablir manuel d'une
   * `SetConfigurationCommand` (undo↔redo inversent quel côté — `before`/`after` — sert de référence
   * "avant" vs de valeur à sauvegarder). Sans effet si `command` n'a pas de métadonnées connues
   * (ex. une `Command` de forme, jamais suivie ici — voir `createTokaMap.ts`). */
  runHistoryChange(command: Command, changeKind: Extract<HistoryChangeKind, "undo" | "redo">): void {
    const meta = this.commandMeta.get(command);
    if (!meta) return;
    // Voir `ShapeSaveTracker.runHistoryChange` — ce undo/redo vient de réécrire l'état "réel" en
    // dehors de `resolveHistoryBeforeConfiguration` : toute ancre encore en attente (rafale
    // `skipHistory` jamais commitée) est donc périmée. Avant `meta.skipSave` ci-dessous, même raison.
    this.historyAnchor = null;
    if (meta.skipSave) return;

    const confirmedConfigurationIfNew = changeKind === "undo" ? meta.afterConfiguration : meta.beforeConfiguration;
    const resultConfiguration = changeKind === "undo" ? meta.beforeConfiguration : meta.afterConfiguration;

    this.trackAndSave(command, meta.skipConfirm, confirmedConfigurationIfNew, resultConfiguration);
  }

  /** Voir `ShapeSaveTracker.trackAndSave` — même logique de succès/échec/dépassement, sans
   * `shapeId` (une seule file). Le rollback réapplique directement `confirmedConfiguration` sur
   * `mapAdapter` (voir `applyConfigurationState`), pas via `command.undo()` : plusieurs commandes
   * peuvent être en vol, on saute directement à la dernière référence confirmée. */
  private trackAndSave(
    command: Command,
    skipConfirm: boolean,
    confirmedConfigurationIfNew: TokaMapConfigurationState,
    resultConfiguration: TokaMapConfigurationState,
  ): void {
    if (this.confirmedConfiguration === null) this.confirmedConfiguration = confirmedConfigurationIfNew;
    const entry: PendingEntry = { command };
    this.pendingCommands.push(entry);

    if (!this.onSaveConfiguration) return;
    this.notify("pending");

    this.onSaveConfiguration(resultConfiguration).then(
      () => {
        const index = this.pendingCommands.indexOf(entry);
        // Déjà retirée par une réussite plus récente : ce résultat tardif ne compte plus.
        if (index === -1) return;
        this.pendingCommands.splice(0, index + 1);
        if (!skipConfirm) this.confirmedConfiguration = resultConfiguration;
        if (this.pendingCommands.length === 0) this.notify("success");
      },
      () => {
        const index = this.pendingCommands.indexOf(entry);
        const isMostRecent = index !== -1 && index === this.pendingCommands.length - 1;
        if (!isMostRecent) {
          console.warn(
            "[onSaveConfiguration] échec ignoré : une modification plus récente est en cours ou a déjà réussi.",
          );
          if (index !== -1) {
            this.pendingCommands.splice(index, 1);
            this.history.discard(command);
          }
          return;
        }
        if (this.confirmedConfiguration) applyConfigurationState(this.mapAdapter, this.confirmedConfiguration);
        for (const pending of this.pendingCommands) this.history.discard(pending.command);
        this.pendingCommands = [];
        // Voir `ShapeSaveTracker.trackAndSave` — ce rollback vient de réécrire l'état "réel" en
        // dehors de `resolveHistoryBeforeConfiguration` : une ancre en attente serait sinon périmée.
        this.historyAnchor = null;
        this.notify("error");
      },
    );
  }

  private notify(status: SaveConfigurationStatusEvent["status"]): void {
    for (const listener of this.listeners) listener({ status });
  }
}
